'use client';

import { useState, useEffect } from 'react';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: number;
  type?: string;
}

interface SessionData {
  id: string;
  messages: Message[];
  intake_state: string;
  intake_data: Record<string, unknown>;
  service_type: string | null;
  created_at: string;
  last_active_at: string;
}

export default function ChatTranscript({ id }: { id: string }) {
  const [session, setSession] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    fetch(`/api/admin/chat/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load chat session');
        return res.json();
      })
      .then(data => setSession(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-red-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <p className="text-foreground/50">{error || 'Session not found'}</p>
      </div>
    );
  }

  const intakeData = session.intake_data || {};
  const clientName = (intakeData.name as string) || 'Unknown';
  const clientEmail = (intakeData.email as string) || '';
  const service = session.service_type || (intakeData.serviceType as string) || 'Not specified';

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-foreground">Chat Transcript</h1>
        <p className="text-sm text-foreground/50 mt-1">
          {clientName} {clientEmail && `(${clientEmail})`} &middot; {service} &middot;{' '}
          {new Date(session.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </p>
      </div>

      <div className="space-y-3">
        {session.messages
          .filter(m => m.role !== 'system')
          .map((msg, i) => (
            <div
              key={msg.id || i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-red-accent text-white rounded-br-md'
                    : 'bg-subtle-bg border border-card-border text-foreground rounded-bl-md'
                }`}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                <div
                  className={`text-[10px] mt-1.5 ${
                    msg.role === 'user' ? 'text-white/50' : 'text-foreground/30'
                  }`}
                >
                  {new Date(msg.timestamp).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
          ))}
      </div>

      <div className="mt-8 pt-4 border-t border-card-border text-xs text-foreground/40 flex items-center justify-between">
        <span>Status: {session.intake_state}</span>
        <span>{session.messages.length} messages</span>
      </div>
    </div>
  );
}
